import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AlertService } from "../alert/service/alert.service";
import { PaymentErrorHandlerService } from "../services/payment-error-handler.service";
import { TranslateService } from "@ngx-translate/core";
import { SKIP_ERROR_ALERT } from "./skip-error-alert.context";
import { environment } from "../../../environments/environment";

/** Opt-in local inbox diagnostics; absent from an environment means off. */
const inboxDiagnostics =
  (environment as { teamInboxPerformanceDiagnostics?: boolean }).teamInboxPerformanceDiagnostics === true;

@Injectable()
export class RestHttpErrorsInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) { }

  private get alertService(): AlertService {
    return this.injector.get(AlertService);
  }

  private get paymentErrorHandler(): PaymentErrorHandlerService {
    return this.injector.get(PaymentErrorHandlerService);
  }

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.handleHttpResponse(event, req);
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => this.handleHttpError(error, req)),
    );
  }

  private isPaymentRequest(url: string): boolean {
    return (
      url.includes("/v1/api/ui/authorizations") ||
      url.includes("/v1/api/ui/payment-links") ||
      url.includes("/v1/api/ui/payment-operations") ||
      url.includes("/v1/api/ui/payment-link") ||
      url.includes("/v1/api/ui/company-payment-config") ||
      url.includes("/v1/api/ui/payment-simulator")
    );
  }

  private handleHttpResponse(
    event: HttpResponse<any>,
    req: HttpRequest<any>,
  ): void {
    // const { body, status } = event;

    // const bodyStatus = body?.status;
    // // const isBodyStatusError =
    // //   bodyStatus && bodyStatus !== "200" && bodyStatus !== 200;

    //   const isBodyStatusError = bodyStatus && (parseInt(bodyStatus) >= 400);

    // if (status !== 200 || isBodyStatusError) {
    //   if (this.isPaymentRequest(req.url)) {
    //     this.paymentErrorHandler.handleApiResponseError(body);
    //   } else {
    //     const message =
    //       body?.message || "Something went wrong with the request.";
    //     this.alertService.showError(message);
    //   }
    // }

    const { body, status } = event;

    // 1. Check if the HTTP status is in the success range (200-299)
    const isHttpSuccess = status >= 200 && status < 300;

    // 2. Check the internal body status only if it exists
    // We only care if the backend explicitly sent a failure code (e.g., 400, 500)
    const bodyStatus = body?.status;
    const isBodyStatusError = bodyStatus && (parseInt(bodyStatus) >= 400);

    // 3. If the HTTP call failed OR the backend sent an explicit error body
    if (!isHttpSuccess || isBodyStatusError) {
      if (req.context.get(SKIP_ERROR_ALERT)) {
        return;
      }
      if (this.isPaymentRequest(req.url)) {
        this.paymentErrorHandler.handleApiResponseError(body);
      } else {
        const message = this.resolveMessage(
          body?.message,
          "APP_GENERIC_ALERTS.SOMETHING_WENT_WRONG",
        );
        this.alertService.showError(message);
      }
    }

  }

  private handleHttpError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
  ): Observable<never> {
    if (inboxDiagnostics) {
      console.warn("INBOX_HTTP_ERROR " + JSON.stringify({
        method: req.method, url: req.url.split("?")[0], status: error.status,
        code: error.error?.i18nErrorCode,
      }));
    }
    if (this.isPaymentRequest(req.url)) {
      this.paymentErrorHandler.handleError(error);
      console.error("HTTP Error: ", error);
      return throwError(() => error);
    }

    if (req.context.get(SKIP_ERROR_ALERT)) {
      return throwError(() => error);
    }

    let errorMessage: string;
    if (error.status === 0) {
      errorMessage = this.translateService.instant(
        "APP_GENERIC_ALERTS.NETWORK_ERROR",
      );
    } else if (error.error) {
      if (typeof error.error === "string") {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      } else {
        errorMessage = this.getServerErrorMessage(error);
      }
      errorMessage = this.resolveMessage(
        errorMessage,
        "APP_GENERIC_ALERTS.SOMETHING_WENT_WRONG",
      );
    } else {
      errorMessage = this.getServerErrorMessage(error);
    }

    this.alertService.showError(errorMessage);
    console.error("HTTP diagnostic:", error.status, req.url);
    return throwError(() => error);
  }

  private getServerErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return this.translateService.instant("APP_GENERIC_ALERTS.BAD_REQUEST");
      case 401:
        return this.translateService.instant("APP_GENERIC_ALERTS.UNAUTHORIZED");
      case 403:
        return this.translateService.instant("APP_GENERIC_ALERTS.FORBIDDEN");
      case 404:
        return this.translateService.instant("APP_GENERIC_ALERTS.NOT_FOUND");
      case 500:
        return this.translateService.instant(
          "APP_GENERIC_ALERTS.INTERNAL_SERVER_ERROR",
        );
      default:
        return this.translateService.instant("APP_GENERIC_ALERTS.HTTP_ERROR", {
          status: error.status,
        });
    }
  }

  /**
   * A backend "message" is only ever safe to show verbatim when it was written
   * for a human — a raw exception/constraint dump (SQL, stack frames, driver
   * error text) accidentally forwarded by a generic exception handler must
   * never reach the UI as-is, since it leaks internal schema/implementation
   * detail. Short-circuits to the generic fallback for anything that looks
   * technical rather than trying to detect every possible shape of leak.
   */
  private looksLikeRawExceptionText(message: string): boolean {
    if (message.length > 200 || message.includes("\n")) {
      return true;
    }
    return /\b(SQL|ERROR:|Exception|constraint|StackTrace|at [a-z0-9_.]+\(.*\.java)\b/i.test(
      message
    );
  }

  private resolveMessage(value: unknown, fallbackKey: string): string {
    const message = typeof value === "string" ? value.trim() : "";
    if (!message) {
      return this.translateService.instant(fallbackKey);
    }
    if (/^[A-Z0-9_]+\.[A-Z0-9_.]+$/.test(message)) {
      const translated = this.translateService.instant(message);
      return translated === message
        ? this.translateService.instant(fallbackKey)
        : translated;
    }
    if (this.looksLikeRawExceptionText(message)) {
      return this.translateService.instant(fallbackKey);
    }
    return message;
  }
}
